import { PropsWithChildren, useEffect, useMemo } from 'react';
import { PX_PER_MINUTE } from '../constants';
import { CalendarGridStore, CalendarGridStoreProvider, useNonReactiveStore } from '../store/calendar-grid.store';
import { CalendarColumn, CalendarColumnGroup, CalendarColumnHeaderType, CalendarEvent } from '../types';
import { convertToIdMap } from '../utils';

type BaseCalendarGridProviderProps = {
  events: CalendarEvent[];
  columnGroups?: CalendarColumnGroup[];
  columnBodyVariant?: CalendarGridStore['columnBodyVariant'];
  pxPerMinute?: CalendarGridStore['pxPerMinute'];
  timeIndicatorLines?: CalendarGridStore['timeIndicatorLines'];
};

type CalendarGridProviderPropsByHeaderType<THeader extends CalendarColumnHeaderType> = BaseCalendarGridProviderProps & {
  headerType: THeader;
  columns: CalendarColumn<THeader>[];
};

export type CalendarGridProviderProps = {
  [THeader in CalendarColumnHeaderType]: CalendarGridProviderPropsByHeaderType<THeader>;
}[CalendarColumnHeaderType];

export const CalendarGridProvider = ({
  children,
  headerType,
  columnBodyVariant = 'grid',
  events,
  columns,
  columnGroups,
  pxPerMinute = PX_PER_MINUTE,
  timeIndicatorLines = 'hourly',
}: PropsWithChildren<CalendarGridProviderProps>) => {
  const initialState = useMemo(() => {
    let gridColumnGroups = columnGroups;
    if (!columnGroups?.length) {
      gridColumnGroups = [
        {
          id: 'default-group',
          name: '',
          columnIds: columns.map((column) => column.id),
          groupOffset: 0,
          currentTime: new Date().toISOString(),
        },
      ];
    }

    const groupIds = gridColumnGroups?.map((group) => group.id) || [];
    const eventsById = convertToIdMap(events);
    const columnsById = convertToIdMap<CalendarColumn>(columns);
    const groupsById = convertToIdMap(gridColumnGroups || []);

    return {
      headerType,
      groupIds,
      groupsById,
      columnsById,
      eventsById,
      pxPerMinute,
      timeIndicatorLines: headerType === 'weekday' ? 'none' : timeIndicatorLines, // time indicator lines are only applicable for non-date-based columns
      hasMultipleGroups: groupIds.length > 1,
      columnBodyVariant,
    };
  }, [columnGroups, events, columns, headerType, pxPerMinute, timeIndicatorLines, columnBodyVariant]);

  return (
    <CalendarGridStoreProvider initialState={initialState}>
      {/* // TODO: this is a workaround to sync the initial state to the store. We should ideally not need this
         // and directly use the initialState in the provider, but that requires some changes
        // in our createProvider util to support dynamic initial state. */}
      <CalendarGridStoreSync state={initialState} />
      {children}
    </CalendarGridStoreProvider>
  );
};

const CalendarGridStoreSync = ({ state }: { state: Partial<CalendarGridStore> }) => {
  const store = useNonReactiveStore();

  useEffect(() => {
    store.setState(state);
  }, [state, store]);

  return null;
};
